import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { RequireRole, RequireOrg, useActiveOrgId } from "./authz";
import OrgPicker from "./components/OrgPicker";
import SuccessPage from './SuccessPage';

import Profile from './profile'; 
import SubmissionHistory from "./components/SubmissionHistory";
import FundBuilderPage from "./components/fundBuilder";
import SecuritiesSpvLlc from './01securitiesSPV_LLC'; 
import RealEstateSpvLlc from './02realEstateSPV_LLC';
import SecuritiesSpvLp from './03securitiesSPV_LP';
import RealEstateSpvLp from './04realEstateSPV_LP';
import SecuritiesFundLp from './05securitiesFund_LP';
import RealEstateFundLp from './06realEstateFund_LP';
import LogoutPage from './logout'; 

import AppendixA from './AppendixA';
import TaxForms from "./taxLinks"; 

import './style/App.css';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);

  const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const activeOrgId = useActiveOrgId();

  // ✅ Always call hooks first
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log("🔍 Full Auth0 user object:", user);
    }
  }, [isAuthenticated, user]);

  // ✅ Redirect only once when unauthenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        authorizationParams: { redirect_uri: window.location.origin },
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return null;

  // 🧙‍♂️ Admins skip OrgPicker entirely
  const isAdmin = user?.["https://til.app/claims/roles"]?.includes("admin");
  if (!isAdmin && !activeOrgId) {
    return <OrgPicker afterPath="/" />;
  }

  // 3. If authenticated, show the main app UI:
  return (
    <Router>
      <div className="App">
      <header className="header flex items-center justify-between px-4 py-0 h-10">
      <div className="logo">
        <img src="/logo.png" alt="Logo" />
        TIL Subscribe
      </div>

  <button className="menu-button" onClick={toggleMenu}>Menu</button>

  <nav className={`nav-links ${menuOpen ? "show" : "hide"}`}>
  <ul>
    <li><Link to="/" onClick={toggleMenu}>Profile</Link></li>

    <RequireRole roles={["admin", "fundAdmin"]}>
      <li><Link to="/submissions" onClick={toggleMenu}>Dashboard</Link></li>
    </RequireRole>

    <RequireRole roles={["admin"]}>
      <li><Link to="/FundBuilderPage" onClick={toggleMenu}>Fund Builder</Link></li>
    </RequireRole>

    {isAdmin ? (
      <>
        <li><Link to="/SecuritiesSPV_LLC_01" onClick={toggleMenu}>Alfredo's Test Fund</Link></li>
        <li><Link to="/RealEstateSPV_LLC_02" onClick={toggleMenu}>HCRE 1177 Subscription Agreement</Link></li>
        <li><Link to="/SecuritiesSPV_LP_03" onClick={toggleMenu}>SecuritiesSPV_LP_03</Link></li>
        <li><Link to="/RealEstateSPV_LP_04" onClick={toggleMenu}>Kasba Fund I, LP</Link></li>
        <li><Link to="/XEIA_SecuritiesFund_LP" onClick={toggleMenu}>XEIA Subscription Aggreement</Link></li>
        <li><Link to="/RealEstateFund_LP_06" onClick={toggleMenu}>RealEstateFund_LP_06</Link></li>
      </>
    ) : (
      <>
        <RequireOrg org="fund-atf">
          <li><Link to="/SecuritiesSPV_LLC_01" onClick={toggleMenu}>Alfredo's Test Fund</Link></li>
        </RequireOrg>
        <RequireOrg org="fund-hcre">
          <li><Link to="/RealEstateSPV_LLC_02" onClick={toggleMenu}>HCRE 1177 Subscription Aggreement</Link></li>
        </RequireOrg>
        <RequireOrg org="fund-atf">
          <li><Link to="/SecuritiesSPV_LP_03" onClick={toggleMenu}>SecuritiesSPV_LP_03</Link></li>
        </RequireOrg>
        <RequireOrg org="fund-kasba">
          <li><Link to="/RealEstateSPV_LP_04" onClick={toggleMenu}>Kasba Fund I, LP</Link></li>
        </RequireOrg>
        <RequireOrg org="fund-xeia">
          <li><Link to="/XEIA_SecuritiesFund_LP" onClick={toggleMenu}>XEIA Subscription Aggreement</Link></li>
        </RequireOrg>
        <RequireOrg org="fund-atf">
          <li><Link to="/RealEstateFund_LP_06" onClick={toggleMenu}>RealEstateFund_LP_06</Link></li>
        </RequireOrg>
      </>
    )}


    {/* <li><Link to="/AppendixA" onClick={toggleMenu}>Appendix A</Link></li> */}
    <li><Link to="/TaxForms" onClick={toggleMenu}>Tax Forms</Link></li>
    <li><Link to="/logout" onClick={toggleMenu}>Logout</Link></li>
  </ul>
</nav>

</header>


        
        <div className="main-content">
          <Routes>
            <Route path="/" element={<Profile />} />

            <Route 
              path="/submissions" 
              element={
                <RequireRole roles={["admin", "fundAdmin"]}>
                    <SubmissionHistory />
                </RequireRole>} />

            <Route 
              path="/FundBuilderPage" 
              element={
                <RequireRole roles={["admin"]}>
                    <FundBuilderPage />
                </RequireRole>} />

            <Route path="/SecuritiesSPV_LLC_01" element={<SecuritiesSpvLlc />} />
            <Route path="/RealEstateSPV_LLC_02" element={<RealEstateSpvLlc />} />
            <Route path="/SecuritiesSPV_LP_03" element={<SecuritiesSpvLp />} />
            <Route path="/RealEstateSPV_LP_04" element={<RealEstateSpvLp />} />
            <Route path="/XEIA_SecuritiesFund_LP" element={<SecuritiesFundLp />} />
            <Route path="/RealEstateFund_LP_06" element={<RealEstateFundLp />} />
            <Route path="/AppendixA" element={<AppendixA />} />
            <Route path="/TaxForms" element={<TaxForms />} />
            <Route path="/Success" element={<SuccessPage />} />
            <Route path="/logout" element={<LogoutPage />} />
          </Routes>
        </div>

        <footer className="footer">
          Property of The Investments Lawyers, PLLC &copy;
        </footer>
      </div>
    </Router>
  );
}

export default App;
